<template>
  <div class="vacation-quiz" ref="quizContainer">
    <div class="mx-auto" style="max-width: 800px;">

      <!-- Quiz Container -->
      <div class="bg-white rounded-xl relative overflow-hidden">

        <!-- Header Image -->
        <div class="quiz-header relative h-48 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80"
            alt="Vacation"
            class="w-full h-full object-cover"
          />
          <div class="absolute top-4 left-4">
            <img
              src="https://uptodatewebdesign.s3.eu-west-3.amazonaws.com/uploads/icon-1741669842731.png"
              alt="Selectair"
              class="w-12 h-12 object-contain bg-white rounded-lg p-1"
            />
          </div>
        </div>

        <!-- Result Modal -->
        <div v-if="showResult" class="p-8 md:p-12">
          <div class="text-center">
            <div class="text-6xl mb-6">{{ result.recommendation.emoji }}</div>
            <h2 class="text-2xl font-bold text-gray-900 mb-4">
              {{ result.recommendation.title }}
            </h2>
            <p class="text-gray-600 mb-8 leading-relaxed">
              {{ result.recommendation.description }}
            </p>
            <button
              @click="goToVacations"
              class="w-full bg-theme text-white font-semibold py-4 px-6 rounded-lg transition-all duration-200 shadow-sm hover:shadow-xl hover:bg-theme-hover"
            >
              Bekijk jouw vakanties →
            </button>
            <button
              @click="resetQuiz"
              class="w-full mt-4 text-gray-500 hover:text-gray-700 py-2 transition-colors"
            >
              Opnieuw beginnen
            </button>
          </div>
        </div>

        <!-- Quiz Form -->
        <div v-else class="p-8 md:p-12">

          <!-- Title -->
          <div class="mb-8">
            <h1 class="text-2xl font-bold text-gray-900 mb-3">
              Hoe ziet jouw ideale vakantie er uit?
            </h1>
            <p class="text-gray-600 text-sm leading-relaxed">
              Vind je het belangrijk om cultuur op te snuiven of wil je liever relaxen op het strand?
              Wil je graag roadtrippen of vind je het fijner als alles in de directe nabijheid van je
              verblijfsplek aanwezig is? Het is niet altijd gemakkelijk om te beslissen waar je op
              vakantie wil. Doe de test en ontdek welke bestemming bij je past. Hou je antwoorden
              ergens bij en bekijk je resultaat op de volgende pagina.
            </p>
          </div>

          <!-- Form -->
          <form @submit.prevent="submitQuiz" class="space-y-8">

            <!-- Question 1: Destination preference -->
            <div class="space-y-3">
              <label class="block text-sm font-semibold text-gray-800">
                Welke soort bestemming heeft jouw voorkeur? <span class="text-red-500">*</span>
              </label>
              <div class="space-y-2">
                <label
                  v-for="option in questions.destination.options"
                  :key="option.value"
                  class="quiz-option"
                  :class="{ 'selected': answers.destination === option.value }"
                >
                  <input
                    type="radio"
                    name="destination"
                    :value="option.value"
                    v-model="answers.destination"
                    class="hidden"
                  />
                  <span class="option-letter">{{ option.value }}.</span>
                  <span class="option-text">{{ option.label }}</span>
                </label>
              </div>
            </div>

            <!-- Question 2: Activity preference -->
            <div class="space-y-3">
              <label class="block text-sm font-semibold text-gray-800">
                Eén van jouw favoriete activiteiten op reis is : <span class="text-red-500">*</span>
              </label>
              <div class="space-y-2">
                <label
                  v-for="option in questions.activity.options"
                  :key="option.value"
                  class="quiz-option"
                  :class="{ 'selected': answers.activity === option.value }"
                >
                  <input
                    type="radio"
                    name="activity"
                    :value="option.value"
                    v-model="answers.activity"
                    class="hidden"
                  />
                  <span class="option-letter">{{ option.value }}.</span>
                  <span class="option-text">{{ option.label }}</span>
                </label>
              </div>
            </div>

            <!-- Question 3: Food preference -->
            <div class="space-y-3">
              <label class="block text-sm font-semibold text-gray-800">
                Wat eet je het liefste ? :
              </label>
              <div class="space-y-2">
                <label
                  v-for="option in questions.food.options"
                  :key="option.value"
                  class="quiz-option"
                  :class="{ 'selected': answers.food === option.value }"
                >
                  <input
                    type="radio"
                    name="food"
                    :value="option.value"
                    v-model="answers.food"
                    class="hidden"
                  />
                  <span class="option-letter">{{ option.value }}.</span>
                  <span class="option-text">{{ option.label }}</span>
                </label>
              </div>
            </div>

            <!-- Question 4: Must-have -->
            <div class="space-y-3">
              <label class="block text-sm font-semibold text-gray-800">
                Het volgende mag zeker aanwezig zijn op jouw vakantiebestemming :
              </label>
              <div class="space-y-2">
                <label
                  v-for="option in questions.mustHave.options"
                  :key="option.value"
                  class="quiz-option"
                  :class="{ 'selected': answers.mustHave === option.value }"
                >
                  <input
                    type="radio"
                    name="mustHave"
                    :value="option.value"
                    v-model="answers.mustHave"
                    class="hidden"
                  />
                  <span class="option-letter">{{ option.value }}.</span>
                  <span class="option-text">{{ option.label }}</span>
                </label>
              </div>
            </div>

            <!-- Question 5: Accommodation -->
            <div class="space-y-3">
              <label class="block text-sm font-semibold text-gray-800">
                Waar zou je graag verblijven ? :
              </label>
              <div class="space-y-2">
                <label
                  v-for="option in questions.accommodation.options"
                  :key="option.value"
                  class="quiz-option"
                  :class="{ 'selected': answers.accommodation === option.value }"
                >
                  <input
                    type="radio"
                    name="accommodation"
                    :value="option.value"
                    v-model="answers.accommodation"
                    class="hidden"
                  />
                  <span class="option-letter">{{ option.value }}.</span>
                  <span class="option-text">{{ option.label }}</span>
                </label>
              </div>
            </div>

            <!-- Submit Button -->
            <div class="pt-4">
              <button
                type="submit"
                :disabled="!isFormValid"
                class="w-full bg-theme text-white font-semibold py-4 px-6 rounded-lg transition-all duration-200 shadow-sm hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-theme-hover"
              >
                VERSTUUR
              </button>
            </div>
          </form>

          <!-- Form Preview Label -->
          <!-- <div class="text-center mt-6">
            <span class="text-gray-400 text-xs">Form Preview</span>
          </div> -->

        </div>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'VacationQuiz',
  data() {
    return {
      showResult: false,
      result: null,

      answers: {
        destination: null,
        activity: null,
        food: null,
        mustHave: null,
        accommodation: null
      },

      questions: {
        destination: {
          options: [
            { value: 'A', label: 'Bekende toeristische trekpleisters' },
            { value: 'B', label: 'Verborgen parels zonder grote massa' },
            { value: 'C', label: 'Ik ga liever niet té ver weg' },
            { value: 'D', label: 'Een zo exotisch mogelijke bestemming' }
          ]
        },
        activity: {
          options: [
            { value: 'A', label: 'Rondkuieren in een wereldstad' },
            { value: 'B', label: 'Cultuur opsnuiven en nieuwe plekken ontdekken' },
            { value: 'C', label: 'Met de wagen de omgeving verkennen' },
            { value: 'D', label: 'Ontspannen aan het zwembad en op het strand' }
          ]
        },
        food: {
          options: [
            { value: 'A', label: 'Tapas' },
            { value: 'B', label: 'Visgerechten en zeevruchten' },
            { value: 'C', label: 'Gastronomische gerechten en goede wijn' },
            { value: 'D', label: 'Gerechten uit de wereldkeuken' }
          ]
        },
        mustHave: {
          options: [
            { value: 'A', label: 'Architecturale meesterwerken en een bruisend centrum' },
            { value: 'B', label: 'Historische gebouwen en mooie landschappen' },
            { value: 'C', label: 'Mondaine badplaatsen en charmante vissersdorpjes' },
            { value: 'D', label: 'Palmbomen, parelwitte stranden en een azuurblauwe zee' }
          ]
        },
        accommodation: {
          options: [
            { value: 'A', label: 'In een hotel midden in de stad vlakbij alle bezienswaardigheden' },
            { value: 'B', label: 'In een charmant boutique hotel' },
            { value: 'C', label: 'In een eigen comfortabel appartement' },
            { value: 'D', label: 'In een exotisch luxe resort vlakbij het strand' }
          ]
        }
      },

      // Quiz mapping configuration
      quizMapping: {
        destination: {
          A: { region: 'europa', travel: ['citytrip'] },
          B: { region: '', travel: [] },
          C: { region: 'europa', travel: [] },
          D: { region: ['azië', 'afrika', 'zuid-amerika', 'oceanie'], travel: [] }
        },
        activity: {
          A: { travel: ['citytrip'] },
          B: { travel: ['rondreis'] },
          C: { travel: ['autovakantie', 'rondreis'] },
          D: { travel: ['all inclusive', 'strandvakantie'] }
        },
        food: {
          A: { region: 'spanje', country: 'spanje' },
          B: { region: 'europa', travel: ['vakantie aan zee'] },
          C: { region: ['europa', 'frankrijk'], country: 'frankrijk' },
          D: { travel: ['rondreis'] }
        },
        mustHave: {
          A: { travel: ['citytrip'] },
          B: { travel: ['rondreis', 'wandelreis'] },
          C: { travel: ['vakantie aan zee'] },
          D: { travel: ['all inclusive', 'strandvakantie'], region: ['caraiben', 'azië'] }
        },
        accommodation: {
          A: { travel: ['citytrip'] },
          B: { travel: [] },
          C: { travel: ['vakantiehuis', 'autovakantie'] },
          D: { travel: ['all inclusive'] }
        }
      },

      destinationProfiles: {
        cityExplorer: {
          regions: ['europa'],
          travel: ['citytrip'],
          countries: ['spanje', 'italië', 'portugal', 'frankrijk']
        },
        cultureSeeker: {
          regions: ['europa'],
          travel: ['rondreis', 'wandelreis'],
          countries: ['portugal', 'kroatië', 'griekenland']
        },
        roadTripper: {
          regions: ['europa'],
          travel: ['autovakantie', 'rondreis'],
          countries: ['frankrijk', 'italië', 'spanje']
        },
        beachLover: {
          regions: ['caraiben', 'azië'],
          travel: ['all inclusive', 'strandvakantie'],
          countries: ['thailand', 'dominicaanse republiek', 'malediven']
        }
      },

      recommendations: {
        cityExplorer: {
          title: 'De Stadsontdekker',
          description: 'Jij houdt van bruisende steden, cultuur en gastronomie. Een citytrip naar een Europese hoofdstad is perfect voor jou!',
          emoji: '🏛️'
        },
        cultureSeeker: {
          title: 'De Cultuurzoeker',
          description: 'Jij zoekt authenticiteit en verborgen parels. Een rondreis vol geschiedenis en prachtige landschappen past bij jou!',
          emoji: '🎭'
        },
        roadTripper: {
          title: 'De Vrijbuiter',
          description: 'Jij houdt van vrijheid en flexibiliteit. Een autovakantie waarbij je zelf je route bepaalt is ideaal voor jou!',
          emoji: '🚗'
        },
        beachLover: {
          title: 'De Zonnekoning(in)',
          description: 'Jij wil ultiem ontspannen met je voeten in het zand. Een all-inclusive strandvakantie is jouw droomreis!',
          emoji: '🏝️'
        }
      }
    }
  },

  computed: {
    shopSettings() {
      return this.$root.shopSettings || {}
    },

    isFormValid() {
      // At minimum, destination and activity should be answered
      return this.answers.destination && this.answers.activity
    }
  },

  methods: {
    countAnswers() {
      const counts = { A: 0, B: 0, C: 0, D: 0 }
      Object.values(this.answers).forEach(answer => {
        if (answer && counts.hasOwnProperty(answer)) {
          counts[answer]++
        }
      })
      return counts
    },

    getDominantProfile() {
      const counts = this.countAnswers()
      const maxCount = Math.max(...Object.values(counts))
      const dominantLetter = Object.keys(counts).find(key => counts[key] === maxCount)

      const profileMap = {
        A: 'cityExplorer',
        B: 'cultureSeeker',
        C: 'roadTripper',
        D: 'beachLover'
      }

      return profileMap[dominantLetter]
    },

    buildFilterParams() {
      const profile = this.getDominantProfile()
      const profileData = this.destinationProfiles[profile]

      const travelTypes = new Set()
      const regions = new Set()

      // Add profile defaults
      profileData.travel.forEach(t => travelTypes.add(t))
      if (Array.isArray(profileData.regions)) {
        profileData.regions.forEach(r => regions.add(r))
      } else if (profileData.regions) {
        regions.add(profileData.regions)
      }

      // Enhance with specific answer mappings
      Object.entries(this.answers).forEach(([question, answer]) => {
        if (!answer) return
        const mapping = this.quizMapping[question]?.[answer]
        if (mapping) {
          if (mapping.travel) {
            mapping.travel.forEach(t => travelTypes.add(t))
          }
          if (mapping.region) {
            if (Array.isArray(mapping.region)) {
              mapping.region.forEach(r => regions.add(r))
            } else {
              regions.add(mapping.region)
            }
          }
        }
      })

      return {
        travel: Array.from(travelTypes),
        region: Array.from(regions)[0] || '',
        profile: profile
      }
    },

    generateRedirectURL() {
      const params = this.buildFilterParams()
      const queryParams = []

      if (params.travel.length > 0) {
        queryParams.push(`travel=${encodeURIComponent(params.travel.join(','))}`)
      }

      if (params.region) {
        queryParams.push(`region=${encodeURIComponent(params.region)}`)
      }

      const queryString = queryParams.length > 0 ? `#/?${queryParams.join('&')}` : ''
      return '/vakanties/' + queryString
    },

    submitQuiz() {
      if (!this.isFormValid) {
        alert('Beantwoord alsjeblieft alle verplichte vragen!')
        return
      }

      const profile = this.getDominantProfile()
      const recommendation = this.recommendations[profile]
      const redirectURL = this.generateRedirectURL()

      this.result = {
        profile,
        recommendation,
        redirectURL
      }

      this.showResult = true

      // Scroll to top of quiz when showing result
      this.$nextTick(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
        // Also try to scroll the quiz container into view
        if (this.$refs.quizContainer) {
          this.$refs.quizContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },

    goToVacations() {
      if (this.result && this.result.redirectURL) {
        // Get the base URL from shopSettings or use default
        const baseUrl = this.shopSettings.baseUrl || 'https://selectair.be'
        // Remove trailing slash from baseUrl if present
        const cleanBaseUrl = baseUrl.replace(/\/$/, '')
        // Build the full absolute URL
        const fullUrl = cleanBaseUrl + this.result.redirectURL

        // Use window.location.href for absolute redirect (works in embedded context)
        window.location.href = fullUrl
      }
    },

    resetQuiz() {
      this.showResult = false
      this.result = null
      this.answers = {
        destination: null,
        activity: null,
        food: null,
        mustHave: null,
        accommodation: null
      }
    }
  }
}
</script>

<style scoped>
.vacation-quiz {
  display: block; /* Clean embed - no background/padding */
}

.quiz-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.quiz-option {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
}

.quiz-option:hover {
  border-color: var(--theme, #e31c18);
  background: #fef2f2;
}

.quiz-option.selected {
  border-color: var(--theme, #e31c18);
  background: #fef2f2;
  box-shadow: 0 0 0 2px rgba(227, 28, 24, 0.1);
}

.option-letter {
  font-weight: 600;
  color: #374151;
  margin-right: 8px;
  min-width: 20px;
}

.option-text {
  color: #374151;
  font-size: 14px;
}

.bg-theme {
  background-color: var(--theme, #e31c18);
}

.bg-theme-hover {
  background-color: var(--theme-hover, #c41916);
}

.hover\:bg-theme-hover:hover {
  background-color: var(--theme-hover, #c41916);
}

.text-theme {
  color: var(--theme, #e31c18);
}

/* Fix text sizes for embedded context */
.text-xs {
  font-size: 12px !important;
  line-height: 16px !important;
}

.text-sm {
  font-size: 14px !important;
  line-height: 20px !important;
}

.text-base {
  font-size: 16px !important;
  line-height: 24px !important;
}

.text-lg {
  font-size: 18px !important;
  line-height: 28px !important;
}

.text-xl {
  font-size: 20px !important;
  line-height: 28px !important;
}

.text-2xl {
  font-size: 24px !important;
  line-height: 32px !important;
}

.text-6xl {
  font-size: 60px !important;
  line-height: 1 !important;
}
</style>
